import { i18n } from '@/services/i18n';
import { UserRole } from '@/interfaces/server-data';
import { hasPermission } from "@/utils/hasPermission";
import clientsData from '@/data/clientsData';

export interface RouteItem {
  id: string;
  title: string;
  href?: string;
  icon: {
    element: string;
    attributes: {
      type: string;
    };
  };
  module?: string;
  open: boolean;
  external?: boolean;
  isHidden?: boolean;
  isLogout?: boolean;
  role?: UserRole;
  child?: RouteItem[];
}

const t = (key: string) => i18n.t(key) as string;

export const getRouterList = (): RouteItem[] => [
  {
    id: 'ResultsMonitor',
    title: t('router.titles.resultsMonitor'),
    href: '/results-monitor',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "desktop",
      }
    },
    module: 'result-monitor',
    open: false,
  },
  {
    id: 'KpiPanel',
    title: t('router.titles.kpiDashboard'),
    href: '/kpi-panel',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "bar-chart",
      }
    },
    module: 'kpi-panel',
    open: false,
  },
  {
    id: 'Calc',
    title: t('router.titles.regularPrices'),
    href: '/calc',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "calculator",
      }
    },
    open: false,
  },
  {
    id: 'ScenarioAnalysisPage',
    title: t('router.titles.scenarioAnalysis'),
    href: '/scenario-analysis',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "apartment",
      }
    },
    module: 'scenarioAnalysis',
    open: false,
  },
  {
    id: 'KviShoppingCart',
    title: t('router.titles.cartsAndKvi'),
    href: '/kviShoppingCart',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "shopping-cart",
      }
    },
    module: 'kviShoppingCart',
    open: false,
  },
  {
    id: 'monitoringPrice',
    title: t('router.titles.priceMonitoring'),
    href: '/monitoring-price',
    isHidden: !hasPermission('PRICES_MONITORING'),
    icon: {
      element: 'a-icon',
      attributes: {
        type: "container"
      }
    },
    open: false,
    module: 'monitoringPrice',
  },
  {
    id: 'OmnichannelStrategyPage',
    title: t('router.titles.omnichannelStrategy'), // Using static title instead of this.$tc
    href: '/omnichannel-strategy',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "fullscreen"
      }
    },
    module: 'omnichannelStrategy',
    open: false,
  },
  {
    id: 'PackageCalc',
    title: t('router.titles.batchCalculation'),
    href: '/package-calc',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "file-text",
      }
    },
    open: false,
  },
  {
    id: 'FixedPrices',
    title: t('router.titles.fixedPrices'),
    href: '/fixed-prices',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "file-done"
      }
    },
    module: 'fixedPrices',
    open: false,
  },
  {
    id: 'Revaluation',
    title: t('router.titles.revaluation'),
    href: '/revaluation',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "dollar"
      }
    },
    module: 'revaluation',
    open: false,
  },
  // {
  //   id: 'Promo',
  //   title: t('router.titles.promoPrices'),
  //   href: '/promo',
  //   icon: {
  //     element: 'a-icon',
  //     attributes: {
  //       type: "percentage"
  //     }
  //   },
  //   module: 'promo',
  //   open: false,
  // },
  {
    id: 'Promotions',
    title: t('router.titles.promoPricesAlt'),
    href: '/promotions',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "percentage"
      }
    },
    module: 'promotions',
    open: false,
  },
  {
    id: 'Sales',
    title: t('router.titles.sales'),
    href: '/sales',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "thunderbolt"
      }
    },
    module: 'sales',
    open: false,
  },
  {
    id: 'Events',
    title: t('router.titles.events'),
    href: '/events',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "calendar"
      }
    },
    module: 'events',
    open: false,
  },
  {
    id: 'Report',
    title: t('router.titles.results'),
    href: '/reports',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "check-circle"
      }
    },
    open: false,
  },
  {
    id: 'Uploads',
    title: t('router.titles.uploads'),
    icon: {
      element: 'a-icon',
      attributes: {
        type: "container"
      }
    },
    open: false,
    module: 'uploads',
    child: [
      {
        id: 'Files',
        title: t('router.titles.salesData'),
        href: '/files',
        icon: {
          element: 'a-icon',
          attributes: {
            type: "container"
          }
        },
        open: false,
      },
      {
        id: 'CompetitorsFiles',
        title: t('router.titles.competitors'),
        href: '/competitors-files',
        icon: {
          element: 'a-icon',
          attributes: {
            type: "container"
          }
        },
        open: false,
      },
      {
        id: 'LastPricesFiles',
        title: t('router.titles.assortment'),
        href: '/lastprices-files',
        icon: {
          element: 'a-icon',
          attributes: {
            type: "container"
          }
        },
        open: false,
      },
      {
        id: 'PromoFiles',
        title: t('router.titles.promo'),
        href: '/promo-files',
        icon: {
          element: 'a-icon',
          attributes: {
            type: "container"
          }
        },
        module: 'download-promo',
        open: false,
      },
      {
        id: 'KviShoppingCart',
        title: t('router.titles.carts'),
        href: '/kviCarts-files',
        icon: {
          element: 'a-icon',
          attributes: {
            type: "container"
          }
        },
        module: 'download-kviCarts',
        open: false,
      },
    ],
  },
  {
    id: 'DataExport',
    title: t('router.titles.dataExport'),
    href: '/data-export',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "export"
      }
    },
    module: 'data-export',
    open: false,
  },
  {
    id: 'MasterData',
    title: t('router.titles.masterData'),
    href: '/master-data',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "form"
      }
    },
    role: UserRole.ROLE_ADMIN,
    open: false,
  },
  {
    id: 'Users',
    title: t('router.titles.users'),
    href: '/users',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "team"
      }
    },
    role: UserRole.ROLE_ADMIN,
    open: false,
  },
  {
    id: 'Settings',
    title: t('router.titles.settings'),
    href: '/settings',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "setting"
      }
    },
    module: 'settings',
    open: false,
  },
  {
    id: 'Help',
    title: t('router.titles.help'),
    href: clientsData.helpLinks[process.env.VUE_APP_CLIENT_NAME] || clientsData.helpLinks.default,
    icon: {
      element: 'a-icon',
      attributes: {
        type: "read"
      }
    },
    open: false,
    external: true,
  },
  {
    id: 'SupportPage',
    title: t('router.titles.support'),
    href: '/support',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "question-circle"
      }
    },
    module: 'support',
    open: false,
  },
  {
    id: 'Logout',
    title: t('router.titles.logout'),
    href: '#',
    icon: {
      element: 'a-icon',
      attributes: {
        type: "logout"
      }
    },
    isLogout: true,
    open: false,
  },
];
